<template>
  <div class="vs-main">
    <!-- Header -->
    <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #EAEDED">
      <button class="vs-btn vs-btn-outline" @click="goBack" style="white-space: nowrap">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" style="width:14px;height:14px"><path d="M10 3L5 8l5 5"/></svg>
        Back
      </button>
      <div class="vs-breadcrumb" style="margin-bottom: 0">
        <a @click="goBack">Bill Analysis</a>
        <span>/</span>
        <span>Bill Detail — {{ period }}</span>
      </div>
    </div>

    <!-- KPI -->
    <div class="vs-kpi-grid vs-kpi-grid-2" style="margin-bottom: 20px">
      <div class="vs-kpi-card">
        <div class="vs-kpi-label">Charge</div>
        <div class="vs-kpi-value">{{ fmt(kpiCharge) }}</div>
        <div class="vs-kpi-change neutral">{{ period }} billing period</div>
      </div>
      <div class="vs-kpi-card">
        <div class="vs-kpi-label">Actual Cost</div>
        <div class="vs-kpi-value" style="color: #D13212">{{ fmt(kpiActual) }}</div>
        <div class="vs-kpi-change down">{{ kpiCharge > 0 ? (((kpiActual - kpiCharge) / kpiCharge * 100).toFixed(1) + '% vs charge') : '' }}</div>
      </div>
    </div>

    <!-- Charts: single border container for alignment -->
    <div style="display: flex; margin-bottom: 20px; border: 1px solid #EAEDED">
      <div style="flex: 1; border-right: 1px solid #EAEDED">
        <div style="padding: 12px 20px; border-bottom: 1px solid #EAEDED; display: flex; justify-content: space-between; align-items: center; position: relative; height: 52px; box-sizing: border-box">
          <span class="vs-section-title">Cost by service</span>
          <span class="vs-info-link" @click="toggleInfo('service')">Info</span>
          <div v-if="infoVisible === 'service'" class="vs-info-popup" style="right: 20px; top: 48px; left: auto">
            按服务分组的费用占比，展示当月各 AWS 服务的 Charge 金额及占比（Top 6 + Others）。
            <span class="vs-info-close" @click="infoVisible = ''">×</span>
          </div>
        </div>
        <div style="padding: 16px 20px">
          <div class="vs-chart-container h320" ref="chartServiceRef"></div>
        </div>
      </div>
      <div style="flex: 1">
        <div style="padding: 12px 20px; border-bottom: 1px solid #EAEDED; display: flex; justify-content: space-between; align-items: center; position: relative; height: 52px; box-sizing: border-box">
          <div style="display: flex; align-items: center; gap: 8px">
            <span class="vs-section-title">Cost by sub-entity</span>
            <span class="vs-info-link" @click="toggleInfo('entity')">Info</span>
          </div>
          <div v-if="infoVisible === 'entity'" class="vs-info-popup" style="right: 20px; top: 48px; left: auto">
            按子实体（Link）分组的费用排名 Top 10，可切换 Charge 和 Actual Cost 视图。
            <span class="vs-info-close" @click="infoVisible = ''">×</span>
          </div>
          <div class="vs-seg-control">
            <button :class="['vs-seg-btn', entityCostMode === 'charge' ? 'active' : '']" @click="switchEntityMode('charge')">Charge</button>
            <button :class="['vs-seg-btn', entityCostMode === 'actual' ? 'active' : '']" @click="switchEntityMode('actual')">Actual Cost</button>
          </div>
        </div>
        <div style="padding: 16px 20px">
          <div class="vs-chart-container h320" ref="chartEntityRef"></div>
        </div>
      </div>
    </div>

    <!-- Link Cost Table -->
    <div class="vs-table-wrap">
      <div class="vs-table-header">
        <span class="vs-table-title">Link cost breakdown</span>
        <div style="display: flex; gap: 8px; align-items: center">
          <div style="position: relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="#AAB7B8" stroke-width="1.6" style="width:14px;height:14px;position:absolute;left:10px;top:50%;transform:translateY(-50%)"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input class="vs-form-input" v-model="searchQuery" placeholder="Search Link ID or customer" style="padding-left: 32px; width: 240px" />
          </div>
          <button class="vs-btn vs-btn-outline" @click="downloadCSV">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            Download CSV
          </button>
        </div>
      </div>
      <table class="vs-tbl">
        <thead>
          <tr>
            <th>LINKID</th><th>Customer</th><th>Payer Name</th><th>Type</th><th>Period</th>
            <th class="r">Charge</th><th class="r">Discount</th><th class="r">Tax</th><th class="r">Total Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filteredLinkData" :key="row.linkId">
            <td class="vs-text-bold">{{ row.linkId }}</td>
            <td>{{ row.customer }}</td><td>{{ row.payer }}</td>
            <td><span class="vs-tag vs-tag-blue">{{ row.type }}</span></td>
            <td>{{ row.period }}</td>
            <td class="r">{{ row.charge }}</td><td class="r vs-text-neg">{{ row.discount }}</td>
            <td class="r">{{ row.tax }}</td><td class="r vs-text-bold">{{ row.total }}</td>
          </tr>
        </tbody>
      </table>
      <div class="vs-table-footer">
        <span class="vs-table-footer-info">Showing 1-{{ filteredLinkData.length }} of {{ linkTotal }} results</span>
        <div class="vs-page-btns"><button class="vs-pg-btn active">1</button></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import * as echarts from 'echarts'
import { ajaxGet } from '@/api/request'
import '@/assets/css/aws-console.scss'

const AWS_PALETTE = ['#6B9BD2', '#8C6BB1', '#067F68', '#EC7211', '#D13212', '#00A1C9', '#879596']
const router = useRouter()
const route = useRoute()

const period = ref(route.query.period || '')
const entityCostMode = ref('charge')
const infoVisible = ref('')

// Store sub-entity data for mode switching
const subEntityData = ref([])

// Read all filter params from BillAnalysis
const billType = route.query.billType || '1'
const agentId = route.query.agent_id || ''
const payerId = route.query.payer_id || ''
const linkId = route.query.link_id || ''

const kpiCharge = ref(0)
const kpiActual = ref(0)
const linkData = ref([])
const linkTotal = ref(0)
const searchQuery = ref('')

// Filtered table data based on search
const filteredLinkData = computed(() => {
  if (!searchQuery.value) return linkData.value
  const q = searchQuery.value.toLowerCase()
  return linkData.value.filter(r =>
    r.linkId.toLowerCase().includes(q) ||
    r.customer.toLowerCase().includes(q) ||
    r.payer.toLowerCase().includes(q)
  )
})

function goBack() { router.push('/billAnalysis') }
function toggleInfo(key) { infoVisible.value = infoVisible.value === key ? '' : key }

function downloadCSV() {
  const headers = ['LINKID', 'Customer', 'Payer Name', 'Type', 'Period', 'Charge', 'Discount', 'Tax', 'Total Cost']
  const rows = filteredLinkData.value.map(r => [r.linkId, r.customer, r.payer, r.type, r.period, r.charge, r.discount, r.tax, r.total])
  const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `bill_detail_${period.value}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function switchEntityMode(mode) {
  entityCostMode.value = mode
  // Re-request sub_entity with new cost_mode
  reloadSubEntity()
}

async function reloadSubEntity() {
  const monthStr = period.value.replace('-', '')
  const params = { month: monthStr, billType, cost_mode: entityCostMode.value }
  if (agentId) params.agent_id = agentId
  if (payerId) params.payer_id = payerId
  if (linkId) params.link_id = linkId

  try {
    const subRes = await ajaxGet({ url: '/stat/analysis/sub_entity/', params })
    const subData = subRes?.data || []
    renderSubEntityChart(subData)
  } catch (e) {
    console.error('reloadSubEntity error:', e)
  }
}

function renderSubEntityChart(subData) {
  if (chartEntityRef.value) {
    const old = echarts.getInstanceByDom(chartEntityRef.value)
    if (old) old.dispose()
  }
  const color = entityCostMode.value === 'charge' ? '#6B9BD2' : '#EC7211'
  // ECharts horizontal bar: Y axis renders bottom-to-top, so first item = bottom
  // Data from API is DESC (largest first), we need largest at top = last in array
  const sorted = [...subData].reverse()
  initChart(chartEntityRef.value, {
    grid: { left: 160, right: 30, top: 10, bottom: 20 },
    xAxis: { type: 'value', axisLabel: { color: '#545B64', formatter: fmtAxis }, splitLine: { lineStyle: { color: '#EAEDED' } } },
    yAxis: { type: 'category', data: sorted.map(r => r.payer_name || r.customer_name || r.link_id), axisLine: { lineStyle: { color: '#EAEDED' } }, axisLabel: { color: '#545B64', fontSize: 11 } },
    series: [{ type: 'bar', data: sorted.map(r => r.amount), barWidth: '50%', itemStyle: { color } }],
    tooltip: { trigger: 'axis', backgroundColor: '#16191F', borderColor: '#16191F', textStyle: { color: '#fff' }, formatter: ps => { let s = ps[0]?.name || ''; ps.forEach(p => { s += '<br/>' + p.marker + ' ' + fmt(p.value) }); return s } },
  })
}
function fmt(v) {
  if (!v || v === 0) return '$0.00'
  if (Math.abs(v) >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M'
  if (Math.abs(v) >= 1e3) return '$' + (v / 1e3).toFixed(2) + 'K'
  return '$' + v.toFixed(2)
}
function fmtAxis(v) {
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M'
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(0) + 'K'
  return v.toFixed(0)
}

const chartServiceRef = ref(null)
const chartEntityRef = ref(null)
const charts = []

function initChart(el, option) {
  if (!el) return null
  const c = echarts.init(el)
  c.setOption(option)
  charts.push(c)
  return c
}

async function loadData() {
  const monthStr = period.value.replace('-', '')
  const params = { month: monthStr, billType }
  if (agentId) params.agent_id = agentId
  if (payerId) params.payer_id = payerId
  if (linkId) params.link_id = linkId

  try {
    // Fetch all three APIs
    const [svcRes, linkRes] = await Promise.all([
      ajaxGet({ url: '/stat/analysis/service_dist/', params }),
      ajaxGet({ url: '/stat/analysis/link_cost/', params }),
    ])

    // Service distribution (doughnut)
    const svcData = svcRes?.data || []
    const totalSvcCharge = svcData.reduce((s, r) => s + (r.charge || 0), 0)
    initChart(chartServiceRef.value, {
      legend: { bottom: 0, textStyle: { color: '#545B64', fontSize: 11 }, itemWidth: 10, itemHeight: 10 },
      series: [{
        type: 'pie', radius: ['55%', '75%'], center: ['50%', '40%'], label: { show: false },
        data: svcData.map((r, i) => ({ value: r.charge, name: `${r.service_name} (${r.share}%)`, itemStyle: { color: AWS_PALETTE[i % AWS_PALETTE.length] } })),
      }],
      tooltip: { trigger: 'item', backgroundColor: '#16191F', borderColor: '#16191F', textStyle: { color: '#fff' }, formatter: p => p.name + '<br/>' + fmt(p.value) },
    })

    // Sub-entity (horizontal bar) — pass cost_mode
    const subParams = { ...params, cost_mode: entityCostMode.value }
    const subRes = await ajaxGet({ url: '/stat/analysis/sub_entity/', params: subParams })
    const subData = subRes?.data || []
    renderSubEntityChart(subData)

    // Link cost table — linkRes is {data: [...], total: N}
    const rows = linkRes?.data || []
    linkData.value = rows.map(r => ({
      linkId: r.link_id,
      customer: r.customer,
      payer: r.payer_name,
      type: r.type || 'AWS',
      period: r.period,
      charge: '$' + (r.charge || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      discount: '$' + (r.discount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      tax: '$' + (r.tax || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      total: '$' + (r.total_cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    }))
    linkTotal.value = linkRes?.total || rows.length

    // KPI: use service dist total for charge, sub_entity for a cross-check
    // Or better: use the cost_graph API for this specific month
    const kpiRes = await ajaxGet({ url: '/stat/analysis/cost_graph/', params: { ...params, year_mode: period.value.substring(0, 4) } })
    const kpiMonths = kpiRes?.months || []
    const thisMonth = kpiMonths.find(m => m.month === monthStr)
    if (thisMonth) {
      kpiCharge.value = thisMonth.charge || 0
      kpiActual.value = thisMonth.actual_cost || 0
    } else {
      // Fallback: sum from service data
      kpiCharge.value = totalSvcCharge
      kpiActual.value = subData.reduce((s, r) => s + (r.amount || 0), 0)
    }
  } catch (e) {
    console.error('BillDetail loadData error:', e)
  }
}

function handleResize() { charts.forEach(c => c.resize()) }

onMounted(() => {
  if (!period.value) {
    const now = new Date()
    period.value = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }
  nextTick(async () => {
    await loadData()
    window.addEventListener('resize', handleResize)
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  charts.forEach(c => c.dispose())
})
</script>

<style lang="scss" scoped>
.vs-main { margin: -24px; padding: 20px 28px 40px; }
/* Info popup */
.vs-info-popup {
  position: absolute; z-index: 100;
  background: #FFFFFF; color: #16191F;
  font-size: 13px; font-weight: 400;
  padding: 12px 16px; border: 1px solid #D5DBDB;
  border-radius: 2px; max-width: 320px;
  line-height: 1.5; box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}
.vs-info-close {
  position: absolute; top: 4px; right: 8px;
  cursor: pointer; font-size: 16px; color: #AAB7B8;
  &:hover { color: #16191F; }
}
</style>
